import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment/moment';

import {LocalizationService} from '../../../../shared/localization/localization.service';
import {Chart, ChartData, LoadChart} from '../key-metrics.interfaces';
import {KeyMetricsTable} from '../key-metrics.table';

import {EChart} from './charts.interface';

export abstract class ChartAbstract implements EChart, LoadChart {

  public title: any;
  public toolbox: any;
  public tooltip: any;
  public legend: any;
  public xAxis: any;
  public yAxis: any;
  public series: any;
  public grid: any;
  public animation: boolean;
  public color: string[];

  public table: KeyMetricsTable;
  public categories: string[];

  protected constructor(public chart: Chart,
                        public translate: TranslateService,
                        public localization: LocalizationService) {
    this.series = [];
  }

  public setBasicChart() {
    this.tooltip = this.getTooltipAxis();
    this.legend = {
      data: this.chart.data.map(item => item.category).filter(category => !!category),
      formatter: (value) => this.translate.instant(value)
    };
    this.yAxis = [
      {
        type: 'value',
        axisLabel: {
          formatter: (value) => this.localization.localizeNumber(value, 'DECIMAL_2_DIGIT')
        }
      }
    ];
  }

  public getTooltip(callbackValue: any, percent: boolean, format: string = 'DECIMAL_2_DIGIT'): any {

    return {
      trigger: 'item',
      formatter: (value) => {
        const percentValue = percent ? ` (${
          this.localization.localizeNumber(value.percent, format)
        }%)` : '';

        return `${
          this.translate.instant(callbackValue(value))
        } : ${
          this.localization.localizeNumber(value.value, format)
        }` + percentValue;
      }
    };
  }

  public getTooltipAxis(format: string = 'DECIMAL_2_DIGIT'): any {
    return {
      trigger: 'axis',
      formatter: (value) => {
        return this.getCharacterValues(value, format);
      }
    };
  }

  public getToolbox(name: string): any {
    return {
      show: true,
      feature: {
        saveAsImage: {
          name: name,
          show: true,
          title: 'Save',
          type: 'png',
          lang: ['Save'],
          pixelRatio: 3
        }
      }
    };
  }

  public getCharacterValues(values: any, format: string): any {
    let result = '';
    result += `${values[0].axisValue}<br/>`;
    values.forEach((element) => {
      result += element.value
        ? `<div style="text-align: left;">${
          element.marker
        } ${
          this.translate.instant(element.seriesName)
        }: ${
          this.localization.localizeNumber(element.value, format)
        }<br/></div>`
        : '';
    });
    return result;
  }

  public getCategories(name: string = 'name', categories?: string): string[] {
    let chartCategories: Set<string>;
    if (categories) {
      chartCategories = new Set<string>(categories.split(';'));
    } else {
      chartCategories = new Set<string>();
      this.chart.data.forEach((d) => chartCategories.add(d[name]));
    }
    return Array.from(chartCategories);
  }

  public getPeriodAggregateData(categories: string[],
                                category: string = 'category',
                                name: string = 'name'): Map<string, number[]> {
    const chartData: Map<string, Map<string, number>> = new Map<string, Map<string, number>>();
    const resultMap: Map<string, number[]> = new Map<string, number[]>();

    this.chart.data.forEach((d) => {
      if (!chartData.get(d[category])) {
        chartData.set(d[category], new Map<string, number>());
      }
      chartData.get(d[category]).set(d[name], d.value);
    });

    chartData.forEach((element: Map<string, number>, key) => {
      const elementArray = [];
      categories.forEach((category) => {
        elementArray.push(
          element.get(category));
      });
      resultMap.set(key, elementArray);
    });
    return resultMap;
  }

  public getAccusData(accus: any): any {
    accus = accus.split(';');

    return this.chart.data.filter((element) => {
      return accus.includes(element.category);
    });
  }

  public getAccusDataWithSort(accus: any): any {
    const accusData = [];
    accus = accus.split(';');

    accus.forEach((element) => {
      this.chart.data.find((item) => {
        if (element === item.category) {
          accusData.push(item);
          return true;
        }
      });
    });

    return accusData;
  }

  public getChartConfig(): EChart {
    return {
      title: this.title,
      toolbox: this.toolbox,
      tooltip: this.tooltip,
      legend: this.legend,
      xAxis: this.xAxis,
      yAxis: this.yAxis,
      series: this.series,
      grid: this.grid,
      animation: this.animation,
      color: this.color
    };
  }

  public createChart(): void {
    this.chart.chartConfig = this.getChartConfig();
    this.chart.entity = this;
    if (this.table) {
      this.createTable();
    }
  }

  public createTable(): void {
    this.chart.table = this.table.configs;
  }

  public formatValue(value: number, isAbs: boolean = false): string {
    return isAbs
      ? this.localization.localizeNumber(Math.abs(value), 'DECIMAL_2_DIGIT')
      : this.localization.localizeNumber(value, 'DECIMAL_2_DIGIT');
  }

  // ------------------------------------------Sort----------------------------------------------------------------------
  public sortCategories(array: string[]): string[] {

    return array.sort((a: string, b: string) => {

      return moment(a, 'MMM YYYY', 'en').isAfter(moment(b, 'MMM YYYY', 'en')) ? 1 : -1;
    });
  }

  public monthSortCall(array: any[], callbackValue: any): any[] {
    return array.sort((a, b) => {
      const compareObj = callbackValue(a, b);

      return moment(compareObj.a, 'MMMM-YYYY', 'en')
        .isAfter(moment(compareObj.b, 'MMMM-YYYY', 'en')) ? 1 : -1;
    });
  }

  public monthSortData(data: ChartData[]): ChartData[] {
    return this.monthSortCall(data, (a, b) => ({
      a: a.name,
      b: b.name
    }));
  }

  // ------------------------------------------Mark----------------------------------------------------------------------

  public getMarkLine(): any {
    return {
      itemStyle: {
        normal: {
          label: {
            formatter: (value) => this.localization.localizeNumber(value.value, 'DECIMAL_2_DIGIT')
          }
        }
      },
      data: [
        {type: 'average', name: 'average'}
      ],
      tooltip: {
        trigger: 'item',
        formatter: (item) => {
          return `${
            this.translate.instant('Average')
          }<br/>${
            this.translate.instant(item.seriesName)
          }: ${
            this.localization.localizeNumber(item.value, 'DECIMAL_2_DIGIT')
          }`;
        }
      }
    };
  }

  public getMarkPoint(): any {
    return {
      itemStyle: {
        normal: {
          label: {
            formatter: (value) => this.localization.localizeNumber(value.value, 'DECIMAL_2_DIGIT'),
            textStyle: {
              color: 'rgba(0, 0, 0, 0)'
            }
          }
        }
      },
      data: [
        {
          type: 'max',
          name: 'max'
        },
        {
          type: 'min',
          name: 'min'
        }
      ]
    };
  }

  // -----------------------------------------------Load-----------------------------------------------------------------
  public createLoadView(withTitle: boolean, withLabel: boolean): void {
    this.createLoadViewDefault(withTitle, withLabel);
  }

  public createLoadViewDefault(withTitle: boolean, withLabel: boolean): void {
    if (withTitle) {
      this.title = {
        show: true,
        text: this.chart.title
      };

      if (this.legend) {
        this.legend.y = 25;
      }
    }
    this.createChart();
  }

  public resetChart(): void {
    this.resetDefault();
  }

  public resetDefault(): void {
    this.title = {
      show: false
    };

    if (this.legend) {
      this.legend.y = 'auto';
    }
    this.createChart();
  }

  // --------------------------------------------------------------------------------------------------------------------
}
